// Flat lists keep part.sentenceMap leaves within the existing six-level sanitizer.
export const MAP_LIMITS = { stages: 12, tokens: 160, relations: 40 };
// Optional v1 scalar: never infer grammar from labels, token errors or argument quality.
export const STAGE_STATUSES = { grammatical: '✓ GRAMMATICAL', error: '✕ ERROR', partial: '◇ PARTIAL — transformation' };
export const ROLES = {
    subject: 'SUBJECT', auxiliary: 'AUXILIARY', modal: 'MODAL', verb: 'VERB',
    verb_phrase: 'VERB PHRASE', connector: 'CONNECTOR', link: 'LINK',
    linking_adverb: 'LINKING ADVERB', linking_phrase: 'LINKING PHRASE',
    object: 'OBJECT', complement: 'COMPLEMENT', detail: 'DETAIL', punctuation: 'PUNCTUATION',
};
export const TW_PACK = 'edexcel-4ea1-transactional-writing';
export const isRecord = v => !!v && typeof v === 'object' && !Array.isArray(v);
const text = v => typeof v === 'string' && v.trim().length > 0;
const idOK = v => typeof v === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(v);
export function newMapId(prefix = 'm') { return prefix + '_' + crypto.randomUUID().slice(0, 8); }
export function tokensForStage(map, id) { return map.tokens.filter(t => t.stageId === id); }
export function moveMapRow(rows, id, direction) {
    const i = rows.findIndex(r => r.id === id), j = i + direction;
    if (i < 0 || j < 0 || j >= rows.length) return rows;
    const next = [...rows]; [next[i], next[j]] = [next[j], next[i]]; return next;
}
export function sentenceMapErrors(map) {
    if (!isRecord(map)) return ['Sentence map must be an object.'];
    const errors = [];
    if (map.version !== 1) errors.push('Sentence map version must be 1.');
    if (!text(map.title)) errors.push('Add a diagram title.');
    if (!['demonstration', 'exercise'].includes(map.mode)) errors.push('Choose demonstration or exercise.');
    if (!['stacked', 'comparison'].includes(map.layout)) errors.push('Choose stacked or comparison layout.');
    for (const key of ['stages', 'tokens', 'relations']) {
        if (key === 'relations' && map[key] === undefined) continue; // sanitizer drops empty arrays
        if (!Array.isArray(map[key]) || map[key].length > MAP_LIMITS[key]) errors.push('Invalid or oversized ' + key + ' list.');
    }
    if (errors.length) return errors;
    if (!map.stages.length || !map.tokens.length) errors.push('Add at least one stage and its blocks.');
    const sets = {};
    for (const key of ['stages', 'tokens', 'relations']) {
        sets[key] = new Set();
        for (const row of map[key] || []) {
            if (!isRecord(row) || !idOK(row.id) || sets[key].has(row.id)) { errors.push('Missing or duplicate ' + key + ' ID.'); continue; }
            sets[key].add(row.id);
            for (const [k, v] of Object.entries(row)) {
                if (typeof v !== 'string' || v.length > (k === 'sentence' ? 6000 : 2000)) errors.push('Invalid ' + key + ' field: ' + k + '.');
            }
        }
    }
    if (typeof map.title !== 'string' || map.title.length > 200) errors.push('Diagram title is limited to 200 characters.');
    if (errors.length) return errors;
    for (const t of map.tokens) {
        if (!sets.stages.has(t.stageId) || !text(t.text) || !Object.hasOwn(ROLES, t.role)) errors.push('Each block needs a stage, text and supported role.');
        if (t.clauseId && (!idOK(t.clauseId) || !text(t.clauseLabel))) errors.push('A clause needs an ID and a label.');
        if (t.clauseLabel && !t.clauseId) errors.push('A clause label needs a clause ID.');
        if (t.status && !['error', 'normal'].includes(t.status)) errors.push('Unknown block status.');
    }
    for (const s of map.stages) {
        if (!text(s.label) || !text(s.sentence)) errors.push('Each stage needs a label and complete plain-text sentence.');
        if (s.status !== undefined && !Object.hasOwn(STAGE_STATUSES, s.status)) errors.push('Unknown stage status. Choose grammatical, error or partial, or omit for legacy neutral.');
        const tokens = tokensForStage(map, s.id);
        if (!tokens.length || tokens.map(t => t.text).join('').replace(/\s/g, '') !== (s.sentence || '').replace(/\s/g, '')) errors.push('Stage ' + s.label + ': blocks must match the complete sentence, including punctuation.');
        const seen = new Set(); let previous = '';
        const labels = new Map();
        for (const t of tokens) {
            const clause = t.clauseId || '';
            if (clause && clause !== previous && seen.has(clause)) errors.push('Clause blocks must be adjacent within their stage.');
            if (clause && labels.has(clause) && labels.get(clause) !== t.clauseLabel) errors.push('Use one label for each clause.');
            if (clause) { seen.add(clause); labels.set(clause, t.clauseLabel); }
            previous = clause;
        }
    }
    for (const r of map.relations || []) {
        const from = map.tokens.find(t => t.id === r.from), to = map.tokens.find(t => t.id === r.to);
        if (!from || !to || from.id === to.id || !text(r.label) || !['movement', 'parallel', 'contrast'].includes(r.kind)) errors.push('Each relationship needs two different blocks, a kind and an explanation.');
        if (r.kind === 'movement' && from && to && (from.stageId === to.stageId || from.text.toLowerCase() !== to.text.toLowerCase())) errors.push('Movement must connect the same token text in two different stages.');
    }
    return [...new Set(errors)];
}
export function sectionSentenceMapErrors(content) {
    if (!['technique', 'analysis'].includes(content?.sectionKind)) return [];
    return (Array.isArray(content?.blocks?.parts) ? content.blocks.parts : []).flatMap((p, i) => (
        isRecord(p) && p.sentenceMap !== undefined
            ? sentenceMapErrors(p.sentenceMap).map(e => 'Content section ' + (i + 1) + ': ' + e) : []
    ));
}
export function blankSentenceMap() {
    const id = newMapId('stage');
    return { version: 1, title: 'Sentence choices', mode: 'demonstration', layout: 'stacked',
        stages: [{ id, label: 'Before', sentence: 'A trial could help.' }],
        tokens: [
            { id: newMapId('token'), stageId: id, text: 'A trial', role: 'subject' },
            { id: newMapId('token'), stageId: id, text: 'could', role: 'modal' },
            { id: newMapId('token'), stageId: id, text: 'help', role: 'verb' },
            { id: newMapId('token'), stageId: id, text: '.', role: 'punctuation' },
        ], relations: [] };
}
